// src/RulesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import RuleCreator from "./RuleCreator";
import RuleConflictModal from "./RuleConflictModal";
import {
  fetchUserRules,
  getRuleById,
  updateRuleById,
  saveUserRule,   // ← NEW
} from "./ruleMatcher";
import { auth, db } from "./firebase";
import { collection, query as fsQuery, where, getDocs, updateDoc, deleteDoc, doc, serverTimestamp, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

// --- responsive helper -------------------------------------------------------
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

// --- utils: normalize timestamps & de-dup by id -----------------------------
const ts = (v) => {
  if (!v) return 0;
  if (typeof v === "string") return Date.parse(v) || 0;
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;            // already ms since epoch
  if (v?.toDate) return v.toDate().getTime();     // Firestore Timestamp
  if (typeof v?.seconds === "number") return v.seconds * 1000; // plain TS-like
  return 0;
};

const uniqById = (arr) => {
  const m = new Map();
  (arr || []).forEach((r) => r?.id && m.set(r.id, r));
  return Array.from(m.values());
};

// 💡 Small inline modal to confirm applying to past transactions
function ApplyToPastModal({ open, count, onYes, onNo }) {
  const isMobile = useIsMobile();
  if (!open) return null;
  return (
    <div
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100 }}
        onClick={onNo}
    >
      <div style={{ background: "#fff", padding: isMobile ? "1rem" : "1.5rem", borderRadius: 12, width: isMobile ? "94%" : 420, maxWidth: 560 }}>
        <h3>Apply to Past Transactions?</h3>
        <p>
          We found <strong>{count}</strong> past transaction{count === 1 ? "" : "s"} that match this rule.
          Do you want to apply the updated category mapping to those now?
        </p>
        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: "1rem" }}>
          <button onClick={onNo} style={{ background: "#333", color: "#fff", border: "none", padding: "0.5rem 1rem", borderRadius: 6, cursor: "pointer" }}>
            No
          </button>
          <button onClick={onYes} style={{ background: "#00B074", color: "#fff", border: "none", padding: "0.5rem 1rem", borderRadius: 6, cursor: "pointer" }}>
            Yes, Apply
          </button>
        </div>
      </div>
    </div>
  );
}

// 🔎 Helper to (roughly) find past matching transactions by vendor/plaidCategory/amount.
// Adjust this to your actual schema & indexes for speed.
async function findPastMatchingTransactions(userId, rule) {
  // Example assumes a "transactions" collection with fields:
  // userId, vendor (string), bankCategory (string), amount (number), date, category, subcategory
  // You can improve this with composite indexes/queries that align to your rule fields.
  const txRef = collection(db, "transactions");
  const q = fsQuery(txRef, where("userId", "==", userId));
  const snap = await getDocs(q);

  const vendor = (rule.vendor || "").toLowerCase().trim();
  const plaid = (rule.plaidCategory || "").toLowerCase().trim();
  const amt = typeof rule.amount === "number" ? Math.abs(rule.amount) : null;
  const amtType = rule.amountMatchType || "exact";
  const amtTol = Number(rule.amountMatchValue || 0);

  const results = [];
  snap.forEach((d) => {
    const tx = d.data();
    const txId = d.id;

    const txVendor = (tx.vendor || "").toLowerCase().trim();
    if (!txVendor) return;
    const vendorMatch =
      (rule.vendorMatchType || "exact") === "exact" ? txVendor === vendor
      : (rule.vendorMatchType === "contains") ? txVendor.includes(vendor)
      : txVendor.startsWith(vendor);
    if (!vendorMatch) return;

    if (rule.plaidCategory) {
      const txPlaid = (Array.isArray(tx.category) ? (tx.category[0] || "") : (tx.bankCategory || "")).toLowerCase().trim();
      const norm = (s) => s.toLowerCase().replace(/[^a-z\s]/g, "").split(/\s+/).filter(Boolean);
      const rWords = norm(plaid);
      const tWords = norm(txPlaid);
      const allMatch = rWords.every((w) => tWords.includes(w));
      if (!allMatch) return;
    }

    if (amt != null) {
      const absTxAmt = Math.abs(Number(tx.amount || 0));
      if (amtType === "exact" && absTxAmt !== amt) return;
      if (amtType === "range" && Math.abs(absTxAmt - amt) > amtTol) return;
    }

    results.push({ id: txId, ...tx });
  });

  return results;
}

export default function RulesPage() {
  const user = auth.currentUser;
  const isMobile = useIsMobile(); // ≤768px stacks & enlarges touch targets
  const [uid, setUid] = useState(auth.currentUser?.uid || null);
  const [accountNameById, setAccountNameById] = useState({});

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUid(u?.uid || null));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!uid) {
      setAccountNameById({});
      return;
    }

    const ref = collection(db, "users", uid, "plaid", "accounts", "byId");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const next = {};
        snap.forEach((docSnap) => {
          const d = docSnap.data() || {};
          const name = d.name || d.official_name || d.officialName || "";
          const mask = d.mask ? String(d.mask) : "";
          const label =
            name && mask ? `${name} • ${mask}` :
            name ? name :
            mask ? mask :
            docSnap.id;

          next[docSnap.id] = label;
        });
        setAccountNameById(next);
      },
      (err) => {
        console.error("[RulesPage] accounts/byId subscription failed:", err);
        setAccountNameById({});
      }
    );

    return () => unsub();
  }, [uid]);

  const [rules, setRules] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const [budgets, setBudgets] = useState([]);

  const [editingRule, setEditingRule] = useState(null); // rule being edited
  const [showEditor, setShowEditor] = useState(false);

  const [conflict, setConflict] = useState(null); // { existing, newRule, onResolve } for RuleConflictModal
  const [applyModal, setApplyModal] = useState({ open: false, matches: [], ruleId: null });

  // 🗑️ Delete confirmation state
  const [deletePrompt, setDeletePrompt] = useState({ open: false, rule: null });

  // ⚠️ Conflict grouping state (Resolve Conflict modal)
  const [groupModal, setGroupModal] = useState({ open: false, ruleIds: [] });

  // ✅ Batch selection state
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Helpers
  const isSelected = (id) => selectedIds.has(id);
  const toggleSelect = (id) =>
    setSelectedIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
  const clearSelection = () => setSelectedIds(new Set());

  // Batch actions
  const [batchDeleteOpen, setBatchDeleteOpen] = useState(false);
  const openBatchDelete = () => selectedIds.size && setBatchDeleteOpen(true);

  const confirmBatchDelete = async () => {
    const toDelete = Array.from(selectedIds);
    if (!toDelete.length) return;
    await Promise.all(toDelete.map(id => deleteDoc(doc(db, "rules", id))));
    const refreshed = uniqById(await fetchUserRules() || []);
    refreshed.sort((a, b) => ts(b.updatedAt || b.createdAt) - ts(a.updatedAt || a.createdAt));
    setRules(refreshed);
    clearSelection();
    setBatchDeleteOpen(false);
  };

  // Reuse ConflictGroupModal for merging selected rules
    const openBatchMerge = () => {
    if (selectedIds.size < 2) return; // need at least two to merge
    setGroupModal({ open: true, ruleIds: Array.from(selectedIds) });
  };

  // Helpers to normalize and test overlap
  const norm = (v) => (v ?? "").toString().trim().toLowerCase();
  const num = (v) => (v == null || v === "" ? null : Number(v));
  const isBlank = (s) => !norm(s);

  // true if two rules can both match the same tx (loose/defensive)
  const rulesOverlap = (a, b) => {
    // Vendor must match exactly (your rules are primarily vendor-driven)
    if (norm(a.vendor) !== norm(b.vendor)) return false;

    // Plaid: overlap if equal OR one is blank (blank means "any")
    const aPlaid = norm(a.plaidCategory);
    const bPlaid = norm(b.plaidCategory);
    const plaidOverlap = (aPlaid === bPlaid) || isBlank(aPlaid) || isBlank(bPlaid);

    // If one rule is plaid-scoped and the other is "any plaid", treat as non-conflict.
    // The specific plaid rule will win via specificity.
    if (aPlaid && !bPlaid) return false;
    if (!aPlaid && bPlaid) return false;

    // Amount: overlap if either is null (means "any"), or exact equal, or inside tolerance if range
    const aAmt = num(a.amount);
    const bAmt = num(b.amount);
    const aType = a.amountMatchType || (aAmt != null ? "exact" : "any");
    const bType = b.amountMatchType || (bAmt != null ? "exact" : "any");

    if (aAmt == null || bAmt == null) {
        // If one is "exact" and the other is "any", we consider it non-conflict.
        // The exact rule is more specific and should take precedence without a warning.
        if ((aType === "exact" && bAmt == null) || (bType === "exact" && aAmt == null)) return false;
        return plaidOverlap; // still consider range-vs-any as overlap
    }
    if (aType === "exact" && bType === "exact") return plaidOverlap && aAmt === bAmt;

    // If any range: treat overlap when |diff| <= max tolerance
    const aTol = Number(a.amountMatchValue || 0);
    const bTol = Number(b.amountMatchValue || 0);
    const diff = Math.abs(aAmt - bAmt);
    return plaidOverlap && diff <= Math.max(aTol, bTol);
  };

  // --- AUTO-MERGE EXACT DUPLICATES -------------------------------------------
  // Build a strict duplicate key (case-insensitive) across ALL matching + mapping fields.
  // If a field is null/blank on one, it must also be null/blank on the other to be considered equal.
  const _norm = (v) => (v ?? "").toString().trim().toLowerCase();
  const _numOrNull = (v) => (v === "" || v == null ? null : Number(v));
  const _orNull = (v) => (v == null || _norm(v) === "" ? null : v);

  function duplicateKey(r) {
    const vendor = _norm(r.vendor);
    const vendorType = (r.vendorMatchType || "exact").toLowerCase();      // exact | contains | startswith
    const plaid = _orNull(r.plaidCategory) ? _norm(r.plaidCategory) : null;

    const amt = _numOrNull(r.amount);                                     // null means "any"
    const amtType = amt == null ? "any" : (r.amountMatchType || "exact").toLowerCase();
    const amtTol = amt == null ? null : Number(r.amountMatchValue || 0);

    const date = _orNull(r.date) ? _norm(r.date) : null;                   // keep if you use date constraints
    const dateType = date == null ? "any" : (r.dateMatchType || "exact").toLowerCase();
    const dateTol = date == null ? null : Number(r.dateMatchValue || 0);

    const toCat = _norm(r.category || "");
    const toSub = _norm(r.subcategory || "");

    // IMPORTANT: identical means everything matches exactly (with the null/any semantics above)
    return [
        `v=${vendor}`,
        `vt=${vendorType}`,
        `pl=${plaid ?? "null"}`,
        `a=${amt ?? "null"}`,
        `at=${amtType}`,
        `av=${amtTol ?? "null"}`,
        `d=${date ?? "null"}`,
        `dt=${dateType}`,
        `dv=${dateTol ?? "null"}`,
        `map=${toCat}|${toSub}`
    ].join("|");
  }

  // Choose a winner to keep (prefer most recently updated, else most recently created, else stable by id)
    function pickWinner(rules) {
    const ts = (r) => Date.parse(r.updatedAt || r.createdAt || 0) || 0;
    return [...rules].sort((a, b) => (ts(b) - ts(a)) || String(b.id).localeCompare(String(a.id)))[0];
  }

  // Faster version if you create a composite index on (userId ASC, matchedRuleId ASC)
  async function reassignMatchedTransactions(userId, winnerId, loserId) {
    const txRef = collection(db, "transactions");
    const q = fsQuery(txRef,
        where("userId", "==", userId),
        where("matchedRuleId", "==", loserId)
    );
    const snap = await getDocs(q);
    const updates = snap.docs.map((d) =>
        updateDoc(doc(db, "transactions", d.id), { matchedRuleId: winnerId })
    );
    await Promise.all(updates);
  }

  // Scan the loaded rules, auto-merge exact duplicate groups, return the refreshed list (or the same list if no changes)
  async function autoMergeExactDuplicatesForUser(ruleList, userId) {
    if (!userId || !Array.isArray(ruleList) || ruleList.length < 2) return ruleList;

    // Group by strict duplicate key
    const buckets = new Map();
    for (const r of ruleList) {
        const key = duplicateKey(r);
        const arr = buckets.get(key) || [];
        arr.push(r);
        buckets.set(key, arr);
    }

    let changed = false;

    for (const [, group] of buckets) {
        if (group.length <= 1) continue;
        // We only auto-merge if ALL members are perfect duplicates (by our key they already are).
        const winner = pickWinner(group);
        const losers = group.filter(x => x.id !== winner.id);

        // Re-point any already-matched transactions from each loser -> winner, then delete the loser rule
        for (const l of losers) {
        await reassignMatchedTransactions(userId, winner.id, l.id);
        await deleteDoc(doc(db, "rules", l.id));
        changed = true;
        }
    }

    if (changed) {
        // Refresh rules after merge
        const refreshed = await fetchUserRules();
        return refreshed || [];
    }
    return ruleList;
  }

  // Map of ruleId -> conflict group (array of ids) and groups list for the whole page
  const { conflictByRuleId, conflictGroups } = useMemo(() => {
    const byId = {};
    const groups = [];

    const rs = rules || [];
    const visited = new Set();

    // Compare rules under same vendor bucket to reduce N^2 cost
    const byVendor = rs.reduce((m, r) => {
        const v = norm(r.vendor) || "#";
        (m[v] ||= []).push(r);
        return m;
  }, {});

  const addGroup = (ids) => {
        // de-dup
        const uniq = Array.from(new Set(ids));
        if (uniq.length <= 1) return;
        // Only keep if they either truly overlap with different targets OR are outright duplicates
        const rulesInGroup = uniq.map((id) => rs.find((x) => x.id === id)).filter(Boolean);
        let meaningful = false;
        for (let i = 0; i < rulesInGroup.length; i++) {
        for (let j = i + 1; j < rulesInGroup.length; j++) {
            const a = rulesInGroup[i], b = rulesInGroup[j];
            if (!rulesOverlap(a, b)) continue;
            const mapDiff = (a.category !== b.category) || (a.subcategory !== b.subcategory);
            const exactDup =
            norm(a.vendor) === norm(b.vendor) &&
            norm(a.plaidCategory) === norm(b.plaidCategory) &&
            (num(a.amount) === num(b.amount)) &&
            (a.amountMatchType || "exact") === (b.amountMatchType || "exact") &&
            (Number(a.amountMatchValue || 0) === Number(b.amountMatchValue || 0)) &&
            a.category === b.category &&
            (a.subcategory || "") === (b.subcategory || "");
            if (mapDiff || exactDup) { meaningful = true; break; }
        }
        if (meaningful) break;
        }
        if (!meaningful) return;

        groups.push(uniq);
        uniq.forEach((id) => (byId[id] = uniq));
  };

  Object.values(byVendor).forEach((bucket) => {
        // simple DFS to collect overlapping components
        for (let i = 0; i < bucket.length; i++) {
        const root = bucket[i];
        if (visited.has(root.id)) continue;
        const stack = [root];
        const comp = [];
        visited.add(root.id);
        while (stack.length) {
            const cur = stack.pop();
            comp.push(cur.id);
            for (const other of bucket) {
            if (visited.has(other.id)) continue;
            if (rulesOverlap(cur, other)) {
                visited.add(other.id);
                stack.push(other);
            }
            }
        }
        addGroup(comp);
        }
  });

    return { conflictByRuleId: byId, conflictGroups: groups };
  }, [rules]);

  // Open/close group modal
  const openConflictGroup = (ruleId) => {
    const ids = conflictByRuleId?.[ruleId];
    if (!ids || !ids.length) return;
    setGroupModal({ open: true, ruleIds: ids });
  };
  const closeConflictGroup = () => setGroupModal({ open: false, ruleIds: [] });

  // Helper: fetch rules by ids
  const rulesById = useMemo(() => {
    const m = {};
    (rules || []).forEach((r) => (m[r.id] = r));
    return m;
  }, [rules]);

  const currentGroupRules = useMemo(
    () => (groupModal.open ? groupModal.ruleIds.map((id) => rulesById[id]).filter(Boolean) : []),
    [groupModal, rulesById]
  );

  // Merge: keep winner, delete others (and re-point matched transactions)
  const mergeConflictGroup = async (winnerId) => {
    const toDelete = (groupModal.ruleIds || []).filter((id) => id !== winnerId);

    // Reassign already-matched transactions loser -> winner
    const uid = auth.currentUser?.uid;
    for (const loserId of toDelete) {
        await reassignMatchedTransactions(uid, winnerId, loserId);
    }

    // Delete losers
    await Promise.all(toDelete.map((id) => deleteDoc(doc(db, "rules", id))));

    // Refresh
    const refreshed = uniqById(await fetchUserRules() || []);
    refreshed.sort((a, b) => ts(b.updatedAt || b.createdAt) - ts(a.updatedAt || a.createdAt));
    setRules(refreshed);
    clearSelection();
    closeConflictGroup();
  };

  // Edit one of the conflicting rules
  const editConflictRule = (ruleId) => {
    closeConflictGroup();
    openEdit(ruleId);
  };

  // Open the delete confirm
  const openDelete = (rule) => setDeletePrompt({ open: true, rule });

  // Close the delete confirm
  const closeDelete = () => setDeletePrompt({ open: false, rule: null });

  // Confirm delete → remove doc from Firestore and refresh list
  const confirmDelete = async () => {
    const id = deletePrompt?.rule?.id;
    if (!id) return;
    await deleteDoc(doc(db, "rules", id)); // same collection used elsewhere
    const refreshed = uniqById(await fetchUserRules() || []);
    refreshed.sort((a, b) => ts(b.updatedAt || b.createdAt) - ts(a.updatedAt || a.createdAt));
    setRules(refreshed);
    // clean up UI state just in case this rule was expanded/edited
    if (expandedId === id) setExpandedId(null);
    if (editingRule?.id === id) closeEdit();
    closeDelete();
  };

  // 🔤 Letter ranges (pager): A–D (default), E–H, I–L, M–P, Q–T, U–Z, Other
  const LETTER_RANGES = [
    { label: "A–D", start: "A", end: "D" },
    { label: "E–H", start: "E", end: "H" },
    { label: "I–L", start: "I", end: "L" },
    { label: "M–P", start: "M", end: "P" },
    { label: "Q–T", start: "Q", end: "T" },
    { label: "U–Z", start: "U", end: "Z" },
    { label: "Other", start: "#", end: "#" },
  ];
  const [rangeIndex, setRangeIndex] = useState(0); // default A–D

  // 🔎 Search query (vendor/category/subcategory)
  const [searchTerm, setSearchTerm] = useState("");

  // Show only rules that are in a conflict group
  const [showOnlyConflicts, setShowOnlyConflicts] = useState(false);

  const [focusRuleId, setFocusRuleId] = useState(null);

  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("focusRuleId");
    if (id) setFocusRuleId(id);
  }, []);

  // First visible letter for a rule (non A–Z goes to "#")
  const firstLetterOf = (r) => {
    const v = (r?.vendor || "").trim();
    const c = v[0] ? v[0].toUpperCase() : "#";
    return /[A-Z]/.test(c) ? c : "#";
  };

  // Text match for search across vendor/category/subcategory
  const matchesQuery = (r, q) => {
    if (!q) return true;
    const hay = [
        r?.vendor || "",
        r?.category || "",
        r?.subcategory || ""
    ].join(" ").toLowerCase();
    return hay.includes(q.toLowerCase());
  };

  // Sorted + filtered list (by vendor alphabetically)
  const sortedFiltered = useMemo(() => {
    const copy = (rules || []).filter((r) => matchesQuery(r, searchTerm));
    copy.sort((a, b) => (a?.vendor || "").localeCompare((b?.vendor || ""), undefined, { sensitivity: "base" }));
    return copy;
  }, [rules, searchTerm]);

  // Apply the A–D / E–H / … window, but ignore range when searching
  const currentRange = LETTER_RANGES[rangeIndex] || LETTER_RANGES[0];
  const inRange = (r) => {
    const ch = firstLetterOf(r);
    if (currentRange.start === "#" && currentRange.end === "#") return ch === "#";
    return ch >= currentRange.start && ch <= currentRange.end;
  };
  const pageRules = useMemo(() => {
    // base list is already search-filtered and A–Z sorted by vendor
    let base = sortedFiltered;

    // When conflicts filter is on, keep only rules that belong to a conflict group
    if (showOnlyConflicts) {
        base = base.filter(r => !!conflictByRuleId?.[r.id]);
    }

    // Ignore letter ranges during search or when showing conflicts
    if (searchTerm.trim() || showOnlyConflicts) return base;

    return base.filter(inRange);
  }, [sortedFiltered, currentRange, searchTerm, showOnlyConflicts, conflictByRuleId]);

  useEffect(() => {
    if (!focusRuleId) return;
    const el = document.getElementById(`rule-row-${focusRuleId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setExpandedId(focusRuleId);
    }
  }, [focusRuleId, pageRules]);

  useEffect(() => {
    async function loadBudgets() {
        const user = auth.currentUser;
        if (!user) return;

        const snap = await getDocs(fsQuery(collection(db, "budgets"), where("userId", "==", user.uid)));
        const fetched = snap.docs.map((d) => {
        const data = d.data();
        // Normalize subcategories to objects with { name, ... }
        const subs = (data.subcategories || []).map((s) =>
            typeof s === "string" ? { name: s } : { name: s.name || "", ...s }
        );
        // A–Z subcategories within each category
        subs.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
        return { id: d.id, category: data.category, subcategories: subs };
        });

        // Group by section, then alphabetical:
        const income = fetched.filter((b) => (b.category || "").toLowerCase() === "income")
                            .sort((a, b) => a.category.localeCompare(b.category, undefined, { sensitivity: "base" }));
        const nonIncome = fetched.filter((b) => (b.category || "").toLowerCase() !== "income")
                                .sort((a, b) => a.category.localeCompare(b.category, undefined, { sensitivity: "base" }));

        setBudgets([...income, ...nonIncome]);
    }
    loadBudgets();
  }, []);

  useEffect(() => {
    (async () => {
        const list = await fetchUserRules();
        const afterMerge = await autoMergeExactDuplicatesForUser(list || [], auth.currentUser?.uid);
        const deduped = uniqById(afterMerge || []);
        deduped.sort((a, b) => ts(b.updatedAt || b.createdAt) - ts(a.updatedAt || a.createdAt));
        setRules(deduped);
    })();
  }, []);

  // Resolve account meta for RuleCreator (name + last4) when opening from Rules page
  const resolveAccountMeta = (rule) => {
    const entries = Object.entries(accountNameById || {});

    // 1) Try to find an account id directly on the rule (support multiple field names)
    let id =
      rule?.accountId ||
      rule?.account_id ||
      rule?.plaidAccountId ||
      rule?.plaid_account_id ||
      rule?.account?.id ||
      "";

    // 2) If we have an id, use the live label map
    const labelFromId = id && accountNameById?.[id] ? accountNameById[id] : "";

    // 3) Otherwise, fall back to any stored label on the rule
    const storedLabel =
      rule?.accountName ||
      rule?.account_name ||
      rule?.accountLabel ||
      rule?.account_label ||
      "";

    // 4) If no id, try reverse-lookup from stored label -> id
    if (!id && storedLabel) {
      const exact = entries.find(([, v]) => v === storedLabel);
      if (exact) {
        id = exact[0];
      } else {
        // Try matching by last 4 digits at end of label
        const m = String(storedLabel).match(/(\d{4})\s*$/);
        const last4 = m ? m[1] : null;
        if (last4) {
          const hit = entries.find(([, v]) => String(v).endsWith(last4));
          if (hit) id = hit[0];
        }
      }
    }

    const label =
      (id && accountNameById?.[id]) ||
      labelFromId ||
      storedLabel ||
      "";

    return { id, label };
  };

  const editorAccountMeta = useMemo(
    () => resolveAccountMeta(editingRule),
    [editingRule, accountNameById]
  );

  const otherRules = useMemo(() => (ruleId) => rules.filter(r => r.id !== ruleId), [rules]);

  const openEdit = async (ruleId) => {
    const rule = await getRuleById(ruleId);
    setEditingRule(rule);
    setShowEditor(true);
  };

  const closeEdit = () => {
    setEditingRule(null);
    setShowEditor(false);
  };

  // Open the creator for a brand-new rule
  const openCreate = () => {
    setEditingRule({
        vendor: "",
        category: "",
        subcategory: "",
        amount: null,
        plaidCategory: "",
        vendorMatchType: "exact",
        amountMatchType: "exact",
        amountMatchValue: 0,
    });
    setShowEditor(true);
  };

  // Save a brand-new rule, refresh list, then prompt apply-to-past
  const saveNewRule = async (payload) => {
    const id = await saveUserRule({ userId: user.uid, ...payload });
    const refreshed = uniqById(await fetchUserRules() || []);
    refreshed.sort((a, b) => ts(b.updatedAt || b.createdAt) - ts(a.updatedAt || a.createdAt));
    setRules(refreshed);

    const winning = await getRuleById(id);
    const matches = await findPastMatchingTransactions(user.uid, winning);
    setApplyModal({ open: true, matches, ruleId: winning.id });
    closeEdit();
  };

  // Check for conflicts vs other rules
  const findConflict = (draft, excludeId) => {
    const normalized = (v) => (v || "").toString().trim().toLowerCase();
    const sameKey = (a, b) =>
      normalized(a.vendor) === normalized(b.vendor) &&
      normalized(a.category) === normalized(b.category) &&
      normalized(a.subcategory) === normalized(b.subcategory) &&
      (a.amount ?? null) === (b.amount ?? null) &&
      normalized(a.plaidCategory) === normalized(b.plaidCategory);

    const conflict = otherRules(excludeId).find((r) => sameKey(r, draft));
    return conflict || null;
  };

  const saveEdits = async (updated) => {
    if (!editingRule) return;

    const draft = { ...editingRule, ...updated };
    // show conflict modal if necessary
    const conflictHit = findConflict(draft, editingRule.id);
    if (conflictHit) {
      setConflict({
        existing: conflictHit,
        newRule: draft,
        onResolve: async (action) => {
          if (action === "replace") {
            // Replace existing rule’s contents with the new one, then delete current? Or update current to match?
            await updateRuleById(conflictHit.id, {
              ...draft,
              // keep the winning rule’s id as conflictHit.id
            });
            // Optionally, you could remove the old (editingRule.id) if you want a true "replace".
            // For simplicity, we’ll just keep both but with conflict resolved by updating the existing one.
          }
          // In either path, re-fetch and continue to apply-to-past prompt
          const list = uniqById(await fetchUserRules() || []);
          list.sort((a, b) => ts(b.updatedAt || b.createdAt) - ts(a.updatedAt || a.createdAt));
          setRules(list);
          setConflict(null);

          // After resolving, prompt to apply to past transactions using the winning rule
          const winning = action === "replace" ? await getRuleById(conflictHit.id) : await getRuleById(editingRule.id);
          const matches = await findPastMatchingTransactions(user.uid, winning);
          setApplyModal({ open: true, matches, ruleId: winning.id });
          closeEdit();
        }
      });
      return;
    }

    // No conflict → update and then prompt apply-to-past
    await updateRuleById(editingRule.id, updated);
    const refreshed = uniqById(await fetchUserRules() || []);
    refreshed.sort((a, b) => ts(b.updatedAt || b.createdAt) - ts(a.updatedAt || a.createdAt));
    setRules(refreshed);

    const winning = await getRuleById(editingRule.id);
    const matches = await findPastMatchingTransactions(user.uid, winning);
    setApplyModal({ open: true, matches, ruleId: winning.id });
    closeEdit();
  };

  const applyToPast = async () => {
    const { matches } = applyModal;
    if (!editingRule) {
      setApplyModal({ open: false, matches: [], ruleId: null });
      return;
    }

    // Build a safe payload once
    const mapCategory = (editingRule.category ?? "").trim() || null;
    const mapSubcat  = (editingRule.subcategory ?? "").trim() || null;

    // optional: add categoryMatch + type like your confirm flow
    const mappingFields = {
      category: mapCategory,           // string or null
      subcategory: mapSubcat,          // string or null
      categoryMatch: (mapCategory || mapSubcat)
        ? {
            type: "rule",              // ✅ include nested type for UI detail/history
            category: mapCategory || "",
            subcategory: mapSubcat || "",
            source: "rule",
          }
        : undefined,                   // omit if both null/blank
      status: "confirmed",             // ✅ top-level for reporting
      type: "rule",                    // ✅ top-level for reporting
    };

    const baseAuditFields = {
      matchedBy: "rule",
      matchedRuleId: applyModal.ruleId,
      confirmedAt: serverTimestamp(),  // ✅ normalized timestamp
    };

    const batchUpdates = matches.map(async (tx) => {
      const txRef = doc(db, "transactions", tx.id);

      // Build the update WITHOUT any undefined values
      const update = {
        ...baseAuditFields,
        // keep these for UX cleanliness
        needsReview: false,
        reviewMessage: null,
      };

      // Apply mapping if provided
      if (mappingFields.category !== null) update.category = mappingFields.category;
      if (mappingFields.subcategory !== null) update.subcategory = mappingFields.subcategory;
      if (mappingFields.categoryMatch) update.categoryMatch = mappingFields.categoryMatch;

      // ✅ Always stamp top-level reporting fields
      update.status = mappingFields.status || "confirmed";
      update.type = mappingFields.type || "rule";

      await updateDoc(txRef, update);
    });

    await Promise.all(batchUpdates);
    setApplyModal({ open: false, matches: [], ruleId: null });
  };

  const cancelApplyToPast = () => setApplyModal({ open: false, matches: [], ruleId: null });

  return (
    <div
      style={{
        marginLeft: isMobile ? "0" : "-5px",
        padding: isMobile ? "1rem 0.75rem 5.5rem" : "1.5rem 1.5rem 4.5rem"
      }}
    >
      {/* Sticky header */}
      <div
        style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: "#fdfdfd",
            paddingBottom: "0.75rem",
            marginBottom: "0.75rem",
            borderBottom: "1px solid #eee"
        }}
        >
        <h2 style={{ marginBottom: "0.25rem" }}>Rules</h2>
        <p style={{ color: "#555", margin: 0 }}>
            Organize and edit your transaction matching rules. Click a rule to expand details or edit it.
        </p>

        <div
          style={{
            marginTop: "0.75rem",
            display: "flex",
            alignItems: isMobile ? "stretch" : "center",
            gap: "0.75rem",
            flexWrap: "wrap"
          }}
        >
            <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search vendor, category, or subcategory…"
            aria-label="Search rules"
            style={{
              flex: isMobile ? "1 1 100%" : "1 1 420px",
              maxWidth: isMobile ? "100%" : 600,
              padding: "0.6rem 0.8rem",
              borderRadius: 8,
              border: "1px solid #ddd",
              outline: "none"
            }}
            />

            <button
            onClick={openCreate}
            style={{
              background: "#00B074",
              color: "#fff",
              border: "none",
              padding: "0.65rem 0.95rem",
              borderRadius: 8,
              cursor: "pointer",
              fontWeight: 600,
              width: isMobile ? "100%" : "auto"
            }}
            >
            + Create Rule
            </button>
            {conflictGroups.length > 0 && (
                <button
                    onClick={() => setShowOnlyConflicts(v => !v)}
                    style={{
                      background: showOnlyConflicts ? "transparent" : "#F4C542",
                      border: `1px solid ${showOnlyConflicts ? "#F4C542" : "#ddd"}`,
                      color: showOnlyConflicts ? "#1a1a1a" : "#333",
                      padding: "0.65rem 0.95rem",
                      borderRadius: 8,
                      cursor: "pointer",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      width: isMobile ? "100%" : "auto"
                    }}
                    title={showOnlyConflicts ? "Show all rules" : "Show only conflicting rules"}
                >
                    {showOnlyConflicts
                    ? "Show all rules"
                    : `See ${conflictGroups.length} rule conflict${conflictGroups.length === 1 ? "" : "s"}`}
                </button>
            )}
        </div>
        {selectedIds.size > 0 && (
                <div
                    style={{
                    marginTop: "0.5rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    flexWrap: "wrap",
                    padding: "0.5rem 0",
                    borderTop: "1px solid #eee"
                    }}
                >
                    <span style={{ fontWeight: 600 }}>{selectedIds.size} selected</span>
                    <button
                    onClick={openBatchMerge}
                    style={{ background: "#00B074", color: "#fff", border: "none", padding: "0.35rem 0.75rem", borderRadius: 6, cursor: "pointer" }}
                    disabled={selectedIds.size < 2}
                    title={selectedIds.size < 2 ? "Select at least two rules to merge" : "Merge selected rules"}
                    >
                    Merge selected
                    </button>
                    <button
                    onClick={openBatchDelete}
                    style={{ background: "#d33", color: "#fff", border: "none", padding: "0.35rem 0.75rem", borderRadius: 6, cursor: "pointer" }}
                    >
                    Delete selected
                    </button>
                    <button
                    onClick={clearSelection}
                    style={{ background: "#eee", color: "#333", border: "none", padding: "0.35rem 0.75rem", borderRadius: 6, cursor: "pointer" }}
                    >
                    Clear
                    </button>
                </div>
            )}
      </div>

      {pageRules.length === 0 ? (
        <div style={{ color: "#666", padding: "0.5rem 0" }}>
            {showOnlyConflicts
            ? "No conflicting rules found."
            : searchTerm
                ? "No rules match your search."
                : "No rules match your search in this range."}
        </div>
        ) : (
        <div style={{ border: "1px solid #eee", borderRadius: 8, overflow: "hidden" }}>
            {pageRules.map((r, idx) => {
            const title = r.vendor
                ? `${r.vendor} → ${r.category}${r.subcategory ? ` / ${r.subcategory}` : ""}`
                : `(Untitled)`;
            const isOpen = expandedId === r.id;
            const rowBg = idx % 2 === 0 ? "#fff" : "#f7f7f7";

            return (
              <div
                key={r.id}
                id={`rule-row-${r.id}`}
                style={{
                  background: rowBg,
                  outline: focusRuleId === r.id ? "2px solid #00B074" : "none",
                  outlineOffset: "-2px"
                }}
              >
                {/* Row header */}
                <div
                  style={{
                    padding: isMobile ? "0.6rem 0.6rem" : "0.5rem 0.75rem",
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "stretch" : "center",
                    justifyContent: isMobile ? "flex-start" : "space-between",
                    gap: isMobile ? "0.6rem" : 0,
                    cursor: "pointer",
                    borderTop: idx === 0 ? "none" : "1px solid #eee"
                  }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <input
                        type="checkbox"
                        checked={isSelected(r.id)}
                        onChange={() => toggleSelect(r.id)}
                        aria-label={`Select rule ${title}`}
                        onClick={(e) => e.stopPropagation()}
                    />
                    <button
                        onClick={() => setExpandedId(isOpen ? null : r.id)}
                        style={{ background: "none", border: "none", textAlign: "left", fontSize: "1rem", cursor: "pointer" }}
                        title="Expand"
                    >
                        <strong>{title}</strong>
                        <div style={{ fontSize: "0.85rem", color: "#666", marginTop: 2 }}>
                        {r.plaidCategory ? `Plaid: ${r.plaidCategory}` : `No Plaid filter`} •
                        {typeof r.amount === "number" ? ` Amount: ${r.amount} (${r.amountMatchType || "exact"})` : ` Any amount`}
                        </div>
                    </button>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        alignItems: "center",
                        width: isMobile ? "100%" : "auto"
                      }}
                    >
                    {selectedIds.size === 0 && conflictByRuleId?.[r.id] && (
                        <button
                        onClick={() => openConflictGroup(r.id)}
                        title="Resolve conflict"
                        style={{
                            background: "#F4C542",
                            color: "#1a1a1a",
                            border: "none",
                            padding: "0.35rem 0.6rem",
                            borderRadius: 6,
                            cursor: "pointer",
                            fontWeight: 600,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.35rem"
                        }}
                        >
                        <span aria-hidden>⚠️</span>
                        <span>Resolve conflict</span>
                        </button>
                    )}

                    <button
                        onClick={() => openEdit(r.id)}
                        style={{
                          background: "#00B074",
                          color: "white",
                          border: "none",
                          padding: "0.5rem 0.8rem",
                          borderRadius: 6,
                          cursor: "pointer",
                          width: isMobile ? "100%" : "auto"
                        }}
                    >
                        Edit
                    </button>

                    <button
                        onClick={() => openDelete(r)}
                          style={{
                            background: "#d33",
                            color: "white",
                            border: "none",
                            padding: "0.5rem 0.8rem",
                            borderRadius: 6,
                            cursor: "pointer",
                            width: isMobile ? "100%" : "auto"
                          }}
                        title="Delete rule"
                    >
                        Delete
                    </button>
                    </div>
                </div>

                {/* Expanded details — same background as the row */}
                {isOpen && (
                    <div style={{ background: rowBg, padding: "0.6rem 0.75rem 0.8rem", borderTop: "1px solid #eee" }}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                          gap: isMobile ? "0.75rem" : "1rem"
                        }}
                      >
                        <div>
                        <h4 style={{ margin: 0 }}>Match Settings</h4>
                        <ul style={{ margin: "0.5rem 0 0 1rem" }}>
                            <li><strong>Vendor:</strong> {r.vendor || "—"} ({r.vendorMatchType || "exact"})</li>
                            <li><strong>Plaid Category:</strong> {r.plaidCategory || "—"}</li>
                            <li><strong>Amount:</strong> {typeof r.amount === "number" ? r.amount : "—"} {r.amountMatchType === "range" ? `± ${r.amountMatchValue || 0}` : ""}</li>
                            <li><strong>Day:</strong> {r.date ?? "—"} {r.dateMatchType === "range" ? `± ${r.dateMatchValue || 0} days` : ""}</li>
                            <li><strong>Maps to:</strong> {r.category} / {r.subcategory || "—"}</li>
                            <li><strong>Updated:</strong> {r.updatedAt ? new Date(r.updatedAt).toLocaleString() : "—"}</li>
                        </ul>
                        </div>

                        <div>
                        <h4 style={{ margin: 0 }}>Matching Transactions</h4>
                        <RuleMatchesPreview rule={r} />
                        </div>
                    </div>
                    </div>
                )}
                </div>
            );
            })}
        </div>
      )}

      {/* Letter-range pager (sticky footer) */}
      {!searchTerm && !showOnlyConflicts && (
        <div 
          style={{
            position: "sticky",
            bottom: 0,
            zIndex: 10,
            background: "#fff",
            padding: isMobile ? "0.6rem 0.25rem" : "0.5rem 0",
            marginTop: "1rem",
            borderTop: "1px solid #eee",
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            alignItems: "center"
          }}
        >
            <span style={{ color: "#555", marginRight: "0.25rem" }}>Jump to:</span>
            {LETTER_RANGES.map((rng, i) => {
            const isActive = i === rangeIndex;
            return (
                <button
                key={rng.label}
                onClick={() => setRangeIndex(i)}
                style={{
                  background: isActive ? "#00B074" : "transparent",
                  border: `1px solid ${isActive ? "#00B074" : "#ddd"}`,
                  color: isActive ? "#fff" : "#333",
                  padding: isMobile ? "0.4rem 0.6rem" : "0.25rem 0.5rem",
                  borderRadius: 999,
                  cursor: "pointer",
                  textDecoration: "none",
                  fontSize: isMobile ? "1rem" : "0.9rem"
                }}
                aria-current={isActive ? "page" : undefined}
                >
                {rng.label}
                </button>
            );
            })}
        </div>
      )}

      {/* Editor */}
      {showEditor && editingRule && (
        <RuleCreator
          tx={{
            amount: 0,
            date: { seconds: Math.floor(Date.now() / 1000) },
            category: [editingRule.plaidCategory || ""],
            vendor: editingRule.vendor || "",
            // ✅ give RuleCreator something real to display
            accountId: editorAccountMeta?.id || "",
            accountName: editorAccountMeta?.label || "",
            transactionType: "",
            mcc: ""
          }}
          budgets={budgets}
          accountNameById={accountNameById}
          initialCategory={editingRule.category}
          initialSubcategory={editingRule.subcategory}
          initialRule={editingRule}
          mode={editingRule?.id ? "edit" : "create"}
          onSave={editingRule?.id ? saveEdits : saveNewRule}
          onClose={closeEdit}
        />
      )}

      {/* Conflict modal */}
      {conflict && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
        }}>
          <div style={{ background: "#fff", padding: "1rem", borderRadius: 12, width: 520 }}>
            <RuleConflictModal
              existing={conflict.existing}
              newRule={conflict.newRule}
              onReplace={() => conflict.onResolve("replace")}
              onAddMore={() => { setConflict(null); /* let the user refine in the editor */ }}
              onCancel={() => setConflict(null)}
            />
          </div>
        </div>
      )}

      {/* Apply-to-past modal */}
      <ApplyToPastModal
        open={applyModal.open}
        count={applyModal.matches.length}
        onYes={applyToPast}
        onNo={cancelApplyToPast}
      />

      {/* Resolve-conflict group modal */}
      <ConflictGroupModal
        open={groupModal.open}
        rules={currentGroupRules}
        onClose={closeConflictGroup}
        onMerge={mergeConflictGroup}
        onEditOne={editConflictRule}
       />

       {/* Delete rule confirm */}
       <ConfirmDeleteRuleModal
            open={deletePrompt.open}
            rule={deletePrompt.rule}
            onCancel={closeDelete}
            onConfirm={confirmDelete}
       />

       {/* Delete many confirm */}
            <ConfirmDeleteManyModal
            open={batchDeleteOpen}
            count={selectedIds.size}
            onCancel={() => setBatchDeleteOpen(false)}
            onConfirm={confirmBatchDelete}
        />

    </div>
  );
}

function ConflictGroupModal({ open, rules, onClose, onMerge, onEditOne }) {
  const isMobile = useIsMobile();
  const [winnerId, setWinnerId] = useState(rules?.[0]?.id || null);
  useEffect(() => {
    setWinnerId(rules?.[0]?.id || null);
  }, [rules]);

  if (!open) return null;

  // ---------- Diff helpers ----------
  const norm = (v) => (v ?? "").toString().trim().toLowerCase();
  const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
  const orNull = (v) => (v == null || norm(v) === "" ? null : v);

  // Canonicalize values for difference detection
  const fieldValue = (r, key) => {
    switch (key) {
      case "vendor": return norm(r.vendor);
      case "vendorMatchType": return (r.vendorMatchType || "exact").toLowerCase();
      case "plaidCategory": return orNull(r.plaidCategory) ? norm(r.plaidCategory) : null;

      case "amount": return numOrNull(r.amount); // null = any amount
      case "amountMatchType": {
        const a = numOrNull(r.amount);
        return a == null ? "any" : (r.amountMatchType || "exact").toLowerCase();
      }
      case "amountMatchValue": {
        const a = numOrNull(r.amount);
        return a == null ? null : Number(r.amountMatchValue || 0);
      }

      case "date": return orNull(r.date) ? norm(r.date) : null;
      case "dateMatchType": {
        const d = orNull(r.date);
        return d == null ? "any" : (r.dateMatchType || "exact").toLowerCase();
      }
      case "dateMatchValue": {
        const d = orNull(r.date);
        return d == null ? null : Number(r.dateMatchValue || 0);
      }

      case "category": return norm(r.category || "");
      case "subcategory": return norm(r.subcategory || "");
      default: return String(r[key] ?? "");
    }
  };

  const FIELDS = [
    { key: "vendor", label: "Vendor" },
    { key: "vendorMatchType", label: "Vendor match" },
    { key: "plaidCategory", label: "Plaid" },
    { key: "amount", label: "Amount" },
    { key: "amountMatchType", label: "Amount match" },
    { key: "amountMatchValue", label: "Amount ±" },
    { key: "date", label: "Day" },
    { key: "dateMatchType", label: "Day match" },
    { key: "dateMatchValue", label: "Day ±" },
    { key: "category", label: "Category" },
    { key: "subcategory", label: "Subcategory" },
  ];

  // Which fields actually differ anywhere in the group?
  const differingKeys = new Set(
    FIELDS
      .filter(({ key }) => {
        const set = new Set((rules || []).map((r) => JSON.stringify(fieldValue(r, key))));
        return set.size > 1; // if more than one unique value, this field differs
      })
      .map((f) => f.key)
  );

  const hasDiffs = differingKeys.size > 0;
  const gold = "#F4C542";               // Trelli secondary
  const goldSoft = "rgba(244, 197, 66, 0.22)";

  const Chip = ({ children, highlight }) => (
    <span
      style={{
        display: "inline-block",
        padding: "0.15rem 0.45rem",
        borderRadius: 999,
        border: `1px solid ${highlight ? gold : "#e5e5e5"}`,
        background: highlight ? goldSoft : "#f7f7f7",
        fontSize: "0.9rem",
        lineHeight: 1.25,
        whiteSpace: "nowrap",
        marginRight: 6,
        marginBottom: 6,
      }}
    >
      {children}
    </span>
  );

  const matchChips = (r) => {
    const chips = [];
    chips.push(
      <Chip key="vendor" highlight={differingKeys.has("vendor") || differingKeys.has("vendorMatchType")}>
        {r.vendor || "—"} ({r.vendorMatchType || "exact"})
      </Chip>
    );
    chips.push(
      <Chip key="plaid" highlight={differingKeys.has("plaidCategory")}>
        {r.plaidCategory ? `Plaid: ${r.plaidCategory}` : "No Plaid filter"}
      </Chip>
    );

    const amt = numOrNull(r.amount);
    const amtType = amt == null ? "any" : (r.amountMatchType || "exact");
    const amtTol = amt == null ? null : Number(r.amountMatchValue || 0);
    chips.push(
      <Chip
        key="amount"
        highlight={
          differingKeys.has("amount") ||
          differingKeys.has("amountMatchType") ||
          differingKeys.has("amountMatchValue")
        }
      >
        {amt == null ? "Any amount" : `Amount: ${amt}${amtType === "range" ? ` ± ${amtTol}` : ""} (${amtType})`}
      </Chip>
    );

    const day = orNull(r.date);
    const dayType = day == null ? "any" : (r.dateMatchType || "exact");
    const dayTol = day == null ? null : Number(r.dateMatchValue || 0);
    chips.push(
      <Chip
        key="date"
        highlight={
          differingKeys.has("date") ||
          differingKeys.has("dateMatchType") ||
          differingKeys.has("dateMatchValue")
        }
      >
        {day == null ? "Any day" : `Day: ${r.date}${dayType === "range" ? ` ± ${dayTol}d` : ""} (${dayType})`}
      </Chip>
    );
    return chips;
  };

  const mapsToChips = (r) => {
    const highlightCat = differingKeys.has("category");
    const highlightSub = differingKeys.has("subcategory");
    return (
      <>
        <Chip highlight={highlightCat}>{r.category || "—"}</Chip>
        <Chip highlight={highlightSub}>{r.subcategory || "—"}</Chip>
      </>
    );
  };

  // Friendly summary of what differs
  const diffSummary = [...differingKeys]
    .map((k) => FIELDS.find((f) => f.key === k)?.label)
    .filter(Boolean)
    .join(", ");

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          padding: isMobile ? "0.75rem 0.9rem" : "1rem 1.25rem",
          borderRadius: 12,
          width: isMobile ? "94%" : 760,
          maxWidth: 920,
          maxHeight: "85vh",
          overflowY: "auto"
        }}
        onClick={(e) => e.stopPropagation()} // keep clicks inside
      >
        <h3 style={{ marginTop: 0 }}>Resolve Conflicting Rules</h3>
        <p style={{ marginTop: 0, color: "#555" }}>
          These rules can match the same transactions. Choose one to keep, or edit others to create more separation.
        </p>

        <div style={{ margin: "0.25rem 0 0.5rem", color: "#333", fontSize: "0.95rem" }}>
          {hasDiffs ? (
            <>
              <span style={{ fontWeight: 600 }}>Differences:</span> {diffSummary}
            </>
          ) : (
            <span style={{ fontWeight: 600 }}>No differences detected (true duplicates).</span>
          )}
        </div>

        <div style={{ border: "1px solid #eee", borderRadius: 8, overflow: "hidden", marginTop: "0.5rem" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "28px 1.2fr 0.8fr",
              gap: "0.5rem",
              padding: "0.5rem",
              background: "#f7f7f7",
              fontWeight: 600
            }}
          >
            <div />
            <div>Match</div>
            <div>Maps To</div>
          </div>

          {rules.map((r) => (
            <div
              key={r.id}
              style={{
                display: "grid",
                gridTemplateColumns: "28px 1.2fr 0.8fr",
                gap: "0.5rem",
                padding: "0.5rem",
                borderTop: "1px solid #eee",
                alignItems: "center"
              }}
            >
              <div>
                <input
                  type="radio"
                  name="winner"
                  checked={winnerId === r.id}
                  onChange={() => setWinnerId(r.id)}
                  aria-label="Keep this rule"
                />
              </div>

              {/* Match (chips with highlights) */}
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                {matchChips(r)}
              </div>

              {/* Maps to (chips with highlights) */}
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                {mapsToChips(r)}
              </div>

              <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                <button
                  onClick={() => onEditOne(r.id)}
                  style={{
                    background: "#333",
                    color: "#fff",
                    border: "none",
                    padding: "0.35rem 0.75rem",
                    borderRadius: 6,
                    cursor: "pointer"
                  }}
                >
                  Edit to create separation
                </button>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "0.75rem" }}>
          <button
            onClick={onClose}
            style={{ background: "#eee", border: "none", padding: "0.45rem 0.9rem", borderRadius: 6, cursor: "pointer" }}
          >
            Cancel
          </button>
          <button
            onClick={() => onMerge(winnerId)}
            disabled={!winnerId}
            style={{ background: "#00B074", color: "#fff", border: "none", padding: "0.45rem 0.9rem", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
          >
            Merge into selected (delete others)
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDeleteRuleModal({ open, rule, onCancel, onConfirm }) {
  const isMobile = useIsMobile();
  if (!open) return null;
  const title = rule
    ? `${rule.vendor || "(No vendor)"} → ${rule.category || "—"}${rule.subcategory ? ` / ${rule.subcategory}` : ""}`
    : "";

  return (
    <div
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", padding: isMobile ? "0.9rem 1rem" : "1rem 1.25rem", borderRadius: 12, width: isMobile ? "94%" : 520, maxWidth: 560 }}
      >
        <h3 style={{ marginTop: 0 }}>Delete this rule?</h3>
        <p style={{ color: "#555" }}>
          <strong>{title}</strong>
        </p>
        <p style={{ color: "#666" }}>
          This will remove the rule from your account. Past transactions already categorized by this rule will remain unchanged.
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
          <button onClick={onCancel} style={{ background: "#eee", border: "none", padding: "0.45rem 0.9rem", borderRadius: 6, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={onConfirm} style={{ background: "#d33", color: "#fff", border: "none", padding: "0.45rem 0.9rem", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
            Delete rule
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDeleteManyModal({ open, count, onCancel, onConfirm }) {
  const isMobile = useIsMobile();
  if (!open) return null;
  return (
    <div
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", padding: isMobile ? "0.9rem 1rem" : "1rem 1.25rem", borderRadius: 12, width: isMobile ? "94%" : 520, maxWidth: 560 }}
      >
        <h3 style={{ marginTop: 0 }}>Delete {count} selected rule{count === 1 ? "" : "s"}?</h3>
        <p style={{ color: "#666" }}>
          This will remove the selected rule{count === 1 ? "" : "s"} from your account. Past transactions already categorized by these rules will remain unchanged.
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
          <button onClick={onCancel} style={{ background: "#eee", border: "none", padding: "0.45rem 0.9rem", borderRadius: 6, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={onConfirm} style={{ background: "#d33", color: "#fff", border: "none", padding: "0.45rem 0.9rem", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
            Delete selected
          </button>
        </div>
      </div>
    </div>
  );
}

// Quick preview table for matches (read-only)
function RuleMatchesPreview({ rule }) {
  const isMobile = useIsMobile();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    (async () => {
      const user = auth.currentUser;
      if (!user) return setRows([]);
      const matches = await findPastMatchingTransactions(user.uid, rule);
      // Show ALL matches; we'll make the list itself scrollable.
      setRows(matches);
    })();
  }, [rule]);

  if (!rows) return <div style={{ color: "#666" }}>Loading…</div>;
  if (rows.length === 0) return <div style={{ color: "#666" }}>No past matches found.</div>;

  const gridCols = isMobile ? "1fr 88px 104px" : "1fr 110px 140px";

  return (
    <div
      style={{
        border: "1px solid #eee",
        borderRadius: 8,
        overflow: "hidden",
        margin: isMobile ? "0 -2px" : 0
      }}
    >
      {/* Header row (static) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: gridCols,
          columnGap: "0.75rem",
          padding: "0.5rem",
          background: "#f7f7f7",
          fontWeight: 600
        }}
      >
        <div>Vendor</div>
        <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>Amount</div>
        <div style={{ whiteSpace: "nowrap" }}>Date</div>
      </div>

      {/* Scrollable body */}
      <div style={{ maxHeight: isMobile ? 260 : 320, overflowY: "auto" }}>
        {rows.map((t) => (
          <div
            key={t.id}
            style={{
              display: "grid",
              gridTemplateColumns: gridCols,
              columnGap: "0.75rem",
              padding: "0.5rem",
              borderTop: "1px solid #eee",
              alignItems: "center"
            }}
          >
            <div title={t.vendor} style={{ wordBreak: "break-word" }}>
              {t.vendor || "—"}
            </div>
            <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
              ${Math.abs(Number(t.amount || 0)).toFixed(2)}
            </div>
            <div style={{ whiteSpace: "nowrap" }}>
              {t.date?.seconds
                ? new Date(t.date.seconds * 1000).toLocaleDateString()
                : "—"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
